import React, { useState, ChangeEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Dropdown,
  FormControl,
  InputGroup,
  DatePicker,
  TextInput,
  TextareaInput,
  Loader,
} from 'components'
import {
  findSelected,
  formatPhoneNumberOnBlur,
  getPhoneNumberMaxLength,
  validateKeyPress,
  trimFirstSpace,
  handleDisableOnPaste,
  thaiCountryCodeOption,
} from 'helpers/common'
import {
  useFindAllTaskType,
  useFindAllTaskIssue,
  filterOptionBuilder,
  PRIORITY_FORM_OPTIONS,
} from 'features/Ticket'

interface CreateTicketFormProps {
  isNoProduct: boolean
}

export const CreateTicketForm: React.FC<CreateTicketFormProps> = ({
  isNoProduct,
}) => {
  const [typeChecking, setTypeChecking] = useState<boolean>(false)
  const [statusPriorityChecking, setStatusPriorityChecking] =
    useState<boolean>(false)
  const [requestDateChecking, setRequestDateChecking] = useState<boolean>(false)
  const [taskIssueChecking, setTaskIssueChecking] = useState<boolean>(false)

  const { t } = useTranslation('ticket')

  const { control, errors, setValue, trigger } = useFormContext()
  const [{ data: taskType, loading: taskTypeLoading }] = useFindAllTaskType()
  const [{ data: taskIssue, loading: taskIssueLoading }] = useFindAllTaskIssue()

  const base = 'ticket-create-form'

  return (
    <div className={base}>
      <FormControl label={t('type.title')} required>
        <InputGroup error={errors.type?.message} variants='secondary'>
          <Controller
            type='text'
            name='type'
            control={control}
            message={errors.type?.message}
            render={(field) => (
              <Dropdown
                disabled={isNoProduct}
                placeholder={t('selectTicketType.placeholder')}
                options={filterOptionBuilder(taskType)}
                onChange={(value) => setValue('type', value.key)}
                isError={Boolean(errors.type?.message)}
                selected={findSelected(
                  filterOptionBuilder(taskType),
                  field.value,
                )}
                onBlur={async () => {
                  setTypeChecking(true)
                  await trigger('type')
                  setTypeChecking(false)
                }}
                withIcon={(taskTypeLoading || typeChecking) && <Loader />}
                ref={field.ref}
              />
            )}
          />
        </InputGroup>
      </FormControl>
      <FormControl label={t('priority.title')} required>
        <InputGroup error={errors.statusPriority?.message} variants='secondary'>
          <Controller
            type='text'
            name='statusPriority'
            control={control}
            message={errors.statusPriority?.message}
            render={(field) => (
              <Dropdown
                disabled={isNoProduct}
                placeholder={t('selectPriority.placeholder')}
                options={PRIORITY_FORM_OPTIONS}
                onChange={(value) => setValue('statusPriority', value.key)}
                isError={Boolean(errors.statusPriority?.message)}
                selected={findSelected(PRIORITY_FORM_OPTIONS, field.value)}
                onBlur={async () => {
                  setStatusPriorityChecking(true)
                  await trigger('statusPriority')
                  setStatusPriorityChecking(false)
                }}
                withIcon={statusPriorityChecking && <Loader />}
                ref={field.ref}
              />
            )}
          />
        </InputGroup>
      </FormControl>

      <FormControl label={t('requestDate.title')} required>
        <Controller
          name='requestDate'
          control={control}
          render={(fields) => (
            <DatePicker
              {...fields}
              disabled={isNoProduct}
              placeholder={t('selectDate.placeholder')}
              isError={Boolean(errors.requestDate?.message)}
              message={errors.requestDate?.message}
              onBlur={async () => {
                setRequestDateChecking(true)
                await trigger('requestDate')
                setRequestDateChecking(false)
              }}
              withIcon={requestDateChecking && <Loader />}
            />
          )}
        />
      </FormControl>

      <FormControl label={t('issueType.title')} required>
        <InputGroup error={errors.taskIssue?.message} variants='secondary'>
          <Controller
            type='text'
            name='taskIssue'
            control={control}
            isError={Boolean(errors.taskIssue?.message)}
            message={errors.taskIssue?.message}
            render={(field) => (
              <Dropdown
                disabled={isNoProduct}
                placeholder={t('selectIssueType.placeholder')}
                options={filterOptionBuilder(taskIssue)}
                onChange={(value) => setValue('taskIssue', value.key)}
                isError={Boolean(errors.taskIssue?.message)}
                selected={findSelected(
                  filterOptionBuilder(taskIssue),
                  field.value,
                )}
                onBlur={async () => {
                  setTaskIssueChecking(true)
                  await trigger('taskIssue')
                  setTaskIssueChecking(false)
                }}
                withIcon={(taskIssueLoading || taskIssueChecking) && <Loader />}
                ref={field.ref}
              />
            )}
          />
        </InputGroup>
      </FormControl>

      <FormControl label={t('contactName.title')}>
        <Controller
          type='text'
          name='contactName'
          control={control}
          render={(fields) => (
            <TextInput
              disabled={isNoProduct}
              placeholder={t('insertContactName.placeholder')}
              {...fields}
              onChange={(e) => {
                fields.onChange(trimFirstSpace(e))
              }}
            />
          )}
        />
      </FormControl>

      <FormControl label={t('phoneNumber.title')}>
        <InputGroup
          variants='ternary'
          error={
            errors.branchContactCountryCode?.message ||
            errors.contactPhoneNumber?.message
          }
        >
          <Controller
            type='text'
            name='branchContactCountryCode'
            control={control}
            render={(field) => (
              <Dropdown
                disabled={isNoProduct}
                className='dropdown--telephone'
                group='right'
                options={[thaiCountryCodeOption]}
                selected={findSelected([thaiCountryCodeOption], field.value)}
                onChange={(value) =>
                  setValue('branchContactCountryCode', value.key)
                }
                isError={
                  Boolean(errors.branchContactCountryCode?.message) ||
                  Boolean(errors.contactPhoneNumber?.message)
                }
              />
            )}
          />
          <Controller
            name='contactPhoneNumber'
            control={control}
            render={(field) => (
              <TextInput
                disabled={isNoProduct}
                type='text'
                className={`${base}__phone-input`}
                group='left'
                placeholder={t('insertPhoneNumber.placeholder')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e.target.value)
                }}
                onFocus={() => {
                  setValue('contactPhoneNumber', field.value.replace('-', ''))
                }}
                value={field.value}
                maxLength={getPhoneNumberMaxLength(field.value) + 1}
                onBlur={formatPhoneNumberOnBlur((value) => {
                  setValue('contactPhoneNumber', value)
                  trigger('contactPhoneNumber')
                })}
                onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                onPaste={(e) => handleDisableOnPaste(e)}
                isError={
                  Boolean(errors.branchContactCountryCode?.message) ||
                  Boolean(errors.contactPhoneNumber?.message)
                }
              />
            )}
          />
        </InputGroup>
      </FormControl>

      <FormControl label={t('remark.title')}>
        <Controller
          name='issueDetail'
          isError={Boolean(errors.issueDetail?.message)}
          message={errors.issueDetail?.message}
          control={control}
          render={(fields) => (
            <TextareaInput
              disabled={isNoProduct}
              placeholder={t('insertDetail.placeholder')}
              className={`${base}__detail-input`}
              hasCountable
              {...fields}
              onChange={(e) => {
                fields.onChange(trimFirstSpace(e))
              }}
            />
          )}
        />
      </FormControl>
    </div>
  )
}
